import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { catchError, finalize, of } from 'rxjs';
import { DatePipe } from '@angular/common';

import { LookupService, ReportGeneratorService, UserManagementService } from '../../../../../app/services/property-matrixV2/services';
import { ActionOption, ColumnConfig, DataTableComponent } from '../../../../../app/shared/components/data-table/data-table.component';
import { NotificationService } from '../../../../../app/shared/services/notification-service/notification.service';
import { UserActionDialogComponent } from '../user-action-dialog/user-action-dialog.component';
import { DateFormatHelperPipe } from '../../../../../app/pipes/date-format-helper.pipe';
import { RoleDto, UserDto } from '../../../../../app/services/property-matrixV2/models';
import { TagsKeyDialogComponent } from '../tags-key-dialog/tags-key-dialog.component';

@Component({
  selector: 'app-endorsed-users',
  templateUrl: './endorsed-users.component.html',
  styleUrls: ['./endorsed-users.component.scss', '../../../../../css/2-modules/_admin-portal.scss']
})
export class EndorsedUsersComponent implements OnInit {

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(DataTableComponent) dataTablePlanner: DataTableComponent;
  @ViewChild(DataTableComponent) dataTableAgent: DataTableComponent;

  loading: boolean = false;
  users: UserDto[] = [];
  usersRoles: RoleDto[] = [];
  listOfUsersAllWithoutFilter: UserDto[] = [];
  listOfUsersUrbanPlanners: UserDto[] = [];
  listOfUsersAgents: UserDto[] = [];
  currentUser: UserDto | undefined;
  fullUser: UserDto | undefined;

  dataUrbanPlanner: any = [
    { fullName: '', email: '', tags: '', metro: '', idNumber: '', mobile: '', actions: '' },
  ];

  dataAgent: any = [
    { fullName: '', email: '', tags: '', metro: '', idNumber: '', mobile: '', actions: '' },
  ];

  columns: ColumnConfig[] = [
    { columnDef: 'fullName', header: 'Full Name', headerAlign: 'left', cellAlign: 'left', width: 30 },
    { columnDef: 'email', header: 'Email', headerAlign: 'left', cellAlign: 'left', width: 10 },
    {
      columnDef: 'tags', header: 'User Role (Tags)', headerAlign: 'left', isFilterable: true, cellAlign: 'left', width: 20,
      headerButton: {
        icon: 'info',
        action: () => this.openTagsDialog(),
        color: 'coral'
      }
    },
    { columnDef: 'metro', header: 'Metro', headerAlign: 'left', isFilterable: true, cellAlign: 'left', width: 20 },
    { columnDef: 'idNumber', header: 'Id Number', headerAlign: 'left', isFilterable: true, cellAlign: 'left', width: 20 },
    { columnDef: 'mobile', header: 'Mobile', headerAlign: 'left', isFilterable: true, cellAlign: 'left', width: 20 },
    { columnDef: 'actions', header: 'Actions', isAction: true, headerAlign: 'center', cellAlign: 'center', width: 10 }
  ];

  actionOptions: ActionOption[] = [
    { label: 'View', action: (user: UserDto) => this.openActionDialog('view', user) },
  ];

  constructor(
    private datePipe: DatePipe,
    public dialog: MatDialog,
    private userManagementService: UserManagementService,
    private notificationService: NotificationService,
    private lookupservice: LookupService,
    private dateFormatHelperPipe: DateFormatHelperPipe,
    private reportService: ReportGeneratorService,
  ) {
    this.sort = new MatSort;
  }

  ngOnInit(): void {
    this.getUserRoles();
    this.getCurrentUser();
    this.getUsersEndorsed();
  }

  formatDate(date: string): string | null {
    return this.dateFormatHelperPipe.transform(date);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataTablePlanner.applyFilter(filterValue);
  }

  applyFilterAgent(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataTableAgent.applyFilter(filterValue);
  }

  private async getUserRoles() {
    this.loading = true;
    this.userManagementService.apiV1UserManagementGetRolesGet()
      .pipe(
        catchError((err) =>
          of(this.notificationService.showErrorMessage("Error", "Could not get User roles."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((roleList: RoleDto[] | void) => {
        if (roleList && roleList.length > 0) {
          this.usersRoles = roleList;
        } else {
          this.notificationService.showErrorMessage('Error', 'Could not fetch Roles.');
        }
      });
  }

  private populateTableData(users: UserDto[]): void {
    this.dataUrbanPlanner = users.map(user => ({
      fullName: `${user.name} ${user.surname}`,
      email: user.email,
      tags: user.userRoles?.map(userRole => userRole.roleName).join(', ') || '',
      metro: user.metro?.description,
      actions: '',
      id: user.id,
      idNumber: user.idNumber,
      mobile: user.mobile
    }));
  }

  private populateTableDataAgent(users: UserDto[]): void {
    this.dataAgent = users.map(user => ({
      fullName: `${user.name} ${user.surname}`,
      email: user.email,
      tags: user.userRoles?.map(userRole => userRole.roleName).join(', ') || '',
      metro: user.metro?.description,
      actions: '',
      id: user.id,
      idNumber: user.idNumber,
      mobile: user.mobile
    }));
  }

  public openActionDialog(actionType: string, user: UserDto): void {
    if (actionType === "view") {
      this.fullUser = this.listOfUsersAllWithoutFilter.find(u => u.id == user.id);
      const dialogRef = this.dialog.open(UserActionDialogComponent, {
        width: '70vw',
        height: '70vh',
        data: { actionType, userDto: this.fullUser }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.getUsersEndorsed();
        }
      });
    } else {
      const dialogRef = this.dialog.open(UserActionDialogComponent, {
        width: '70vw',
        height: '70vh',
        data: { actionType, userDto: user }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.getUsersEndorsed();
        }
      });
    }
  }

  private getCurrentUser(): void {
    this.loading = true;
    const UserData = this.userManagementService.apiV1UserManagementGetCurrentUserLoggedInUserGet()
      .pipe(
        catchError((err) =>
          of(this.notificationService.showWarningMessage('No Current User', 'No current user found.'))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: any | void) => {
        this.currentUser = result;
      });
  }

  private async getUsersEndorsed() {
    this.loading = true;
    this.lookupservice.apiV1LookupGetAllEndorsedUsersGet()
      .pipe(
        catchError((err) =>
          of(this.notificationService.showErrorMessage("Error", "Could not get tUser roles."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((results: UserDto[] | void) => {
        if (results && results.length > 0) {
          this.listOfUsersAllWithoutFilter = results;
          this.listOfUsersUrbanPlanners = results.filter(d => d.userRoles?.some(r => r.roleName == "Network Planner"));
          this.populateTableData(this.listOfUsersUrbanPlanners);
          this.dataTablePlanner.dataSource = new MatTableDataSource(this.dataUrbanPlanner);
          this.dataTablePlanner.dataSource.sort = this.dataTablePlanner.sort;
          this.dataTablePlanner.dataSource.paginator = this.dataTablePlanner.paginator;

          this.dataTablePlanner.dataSource.filterPredicate = (data: any, filter: string) => {
            const searchStr = filter.toLowerCase();
            return data.fullName.toLowerCase().includes(searchStr) ||
              data.email.toLowerCase().includes(searchStr) ||
              data.tags.toLowerCase().includes(searchStr) ||
              data.metro.toLowerCase().includes(searchStr) ||
              data.mobile.toLowerCase().includes(searchStr) ||
              data.idNumber.toLowerCase().includes(searchStr);
          };

          this.listOfUsersAgents = results.filter(d => d.userRoles?.some(r => r.roleName == "Network Agent" || r.roleName == "Principal Agent"));
          this.populateTableDataAgent(this.listOfUsersAgents);
          this.dataTableAgent.dataSource = new MatTableDataSource(this.dataAgent);
          this.dataTableAgent.dataSource.sort = this.dataTableAgent.sort;
          this.dataTableAgent.dataSource.paginator = this.dataTableAgent.paginator;

          this.dataTableAgent.dataSource.filterPredicate = (data: any, filter: string) => {
            const searchStr = filter.toLowerCase();
            return this.dataAgent.fullName.toLowerCase().includes(searchStr) ||
              data.email.toLowerCase().includes(searchStr) ||
              data.tags.toLowerCase().includes(searchStr) ||
              data.metro.toLowerCase().includes(searchStr) ||
              data.mobile.toLowerCase().includes(searchStr) ||
              data.idNumber.toLowerCase().includes(searchStr);
          };
        } else {
          this.notificationService.showErrorMessage('Error', 'Could not fetch Roles.');
        }
      });
  }

  public openTagsDialog(): void {
    this.dialog.open(TagsKeyDialogComponent, {
      width: '35vw',
      height: '80vh'
    });
  }
}
